import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "../lib/api";

const STATUS = {
  awaiting_payment: { label: "Awaiting payment", icon: Clock, cls: "text-amber-300" },
  submitted: { label: "Payment submitted — verifying", icon: Loader2, cls: "text-violet-300" },
  paid: { label: "Paid", icon: CheckCircle2, cls: "text-emerald-400" },
  cancelled: { label: "Cancelled", icon: XCircle, cls: "text-rose-400" },
};

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied to clipboard"));

export default function OrderPage() {
  const { ref } = useParams();
  const [o, setO] = useState(null);
  const [tx, setTx] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = () =>
      api.get(`/orders/${ref}`, { params: { _: Date.now() } })
        .then((r) => setO(r.data))
        .catch((e) => { if (e?.response?.status === 404) setO(false); });
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [ref]);

  if (o === false) return <div className="mx-auto max-w-3xl px-5 py-20 text-zinc-400">Order not found.</div>;
  if (!o) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-violet-400" /></div>;

  const st = STATUS[o.status] || STATUS.awaiting_payment;
  const Icon = st.icon;
  const amount = o.crypto_amount ? `${o.crypto_amount} ${o.coin}` : `${money(o.total)} in ${o.coin}`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/orders/${ref}/tx`, { tx_hash: tx });
      setO(r.data);
      toast.success("Transaction submitted");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-3xl px-5 py-10" data-testid="order-page">
      <p className="text-xs uppercase tracking-[0.3em] text-zinc-400">Order</p>
      <h1 className="mt-2 font-display text-3xl font-semibold text-white sm:text-4xl">{o.product_title} × {o.quantity}</h1>
      <div data-testid="order-status" className={`mt-4 flex items-center gap-2 ${st.cls}`}><Icon className={`h-5 w-5 ${o.status === "submitted" ? "animate-spin" : ""}`} /> {st.label}</div>
      <p className="mt-2 text-sm text-zinc-400">Save this page link — it is your receipt. Ref: <span className="font-mono text-zinc-300" data-testid="order-ref">{o.ref}</span></p>

      {o.status === "paid" && (
        <div data-testid="order-delivery" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6">
          <div className="mb-2 text-sm uppercase tracking-[0.2em] text-emerald-300">Payment confirmed</div>
          <p className="text-sm text-emerald-100">Your order is confirmed.</p>
        </div>
      )}

      {o.auto && (o.status === "awaiting_payment" || o.status === "submitted") ? (
        <div className="mt-8 grid gap-8 rounded-2xl border border-white/[0.07] bg-[#1a1633] p-6 sm:grid-cols-[auto_1fr]" data-testid="auto-payment-box">
          <div className="mx-auto rounded-2xl bg-white p-3" data-testid="crypto-qr"><QRCodeSVG value={o.address} size={150} /></div>
          <div className="min-w-0">
            <div className="text-sm uppercase tracking-[0.2em] text-zinc-400">Send exactly</div>
            <div data-testid="crypto-amount" className="mt-1 font-display text-2xl font-semibold text-white">{amount}</div>
            <div className="text-sm text-zinc-400">{money(o.total)} USD</div>
            <div className="mt-5 text-sm uppercase tracking-[0.2em] text-zinc-400">To this {o.coin} address</div>
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 p-3">
              <span data-testid="crypto-address" className="min-w-0 flex-1 break-all font-mono text-sm text-zinc-200">{o.address}</span>
              <button onClick={() => copy(o.address)} data-testid="crypto-copy-address-button" className="rounded-lg p-2 text-violet-300 hover:bg-white/5"><Copy className="h-4 w-4" /></button>
            </div>
            <p className="mt-4 text-sm text-zinc-300">Your payment is detected automatically — this page updates on its own once the network confirms.</p>
            {o.invoice_url && <a href={o.invoice_url} target="_blank" rel="noreferrer" data-testid="open-invoice-button" className="mt-3 inline-block text-sm text-violet-300 hover:text-white">Open NOWPayments page instead</a>}
          </div>
        </div>
      ) : null}

      {!o.auto && !o.invoice_url && (o.status === "awaiting_payment" || o.status === "submitted") && (
        <div className="mt-8 grid gap-8 rounded-2xl border border-white/[0.07] bg-[#1a1633] p-6 sm:grid-cols-[auto_1fr]">
          <div className="mx-auto rounded-2xl bg-white p-3" data-testid="crypto-qr"><QRCodeSVG value={o.address} size={150} /></div>
          <div className="min-w-0">
            <div className="text-sm uppercase tracking-[0.2em] text-zinc-400">Send exactly</div>
            <div data-testid="crypto-amount" className="mt-1 font-display text-2xl font-semibold text-white">{amount}</div>
            <div className="text-sm text-zinc-400">{money(o.total)} USD</div>
            <div className="mt-5 text-sm uppercase tracking-[0.2em] text-zinc-400">To this {o.coin} address</div>
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 p-3">
              <span data-testid="crypto-address" className="min-w-0 flex-1 break-all font-mono text-sm text-zinc-200">{o.address}</span>
              <button onClick={() => copy(o.address)} data-testid="crypto-copy-address-button" className="rounded-lg p-2 text-violet-300 hover:bg-white/5"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
          <form onSubmit={submit} className="sm:col-span-2">
            <div className="mb-2 text-sm text-zinc-300">After sending, paste your transaction hash (TXID):</div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input data-testid="tx-hash-input" required minLength={6} value={tx} onChange={(e) => setTx(e.target.value)} placeholder={o.tx_hash || "Transaction hash"} className="field font-mono text-sm" />
              <button data-testid="tx-submit-button" disabled={busy} className="btn-primary shrink-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I've paid"}</button>
            </div>
          </form>
        </div>
      )}
      <Link to="/" data-testid="order-back-shop" className="mt-10 inline-block text-sm text-violet-300 hover:text-white">← Continue shopping</Link>
    </main>
  );
}
